'use client';

import { styled } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useCallback, useEffect, useRef, useState } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

import styles from '@/styles/latest-news.module.css';
import NewsCard from './news-card';
import { getMobileDetect } from '@/utils/mobile-detect';
import { updateSlickSlideFocusability } from '@/utils/slick-a11y';

const SlickSlider = Slider as unknown as React.ComponentType<{
  children?: React.ReactNode;
  className?: string;
  [key: string]: unknown;
}>;

const NewsTabs = styled(Tabs)({
  borderBottom: `none`,
  '& .MuiTabs-indicator': {
    backgroundColor: `transparent`,
  },
});

const NewsTab = styled((props: StyledTabProps) => <Tab disableRipple {...props} />)(({ theme }) => ({
  textTransform: `none`,
  minWidth: 0,
  [theme.breakpoints.up(`lg`)]: {
    minWidth: 0,
    fontSize: `14px`,
    padding: `12px 16px`,
  },
  fontWeight: `600`,
  color: `var(--text-color-darker)`,
  fontFamily: [`"Mulish"`, `sans-serif`].join(`,`),
  fontSize: `12px`,
  marginRight: `5px`,
  padding: `12px 14px`,
  '&:hover': {
    color: `var(--brand-purple-secondary)`,
    opacity: 1,
  },
  '&.Mui-selected': {
    color: `var(--brand-purple-secondary)`,
    fontWeight: `800`,
    backgroundColor: `var(--brand-yellow-primary)`,
    borderRadius: `100px`,
  },
}));

interface StyledTabProps {
  label: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div hidden={value !== index} {...other}>
      {value === index && <div className="tab_content">{children}</div>}
    </div>
  );
}

export default function LatestNews(props: { categories: any; tabPanel: any }) {
  const [tabIndex, setTabIndex] = useState(0);
  const [enabled, setEnabled] = useState(false);
  const sliderRootRef = useRef<HTMLDivElement>(null);

  const updateSlideFocusability = useCallback(() => {
    updateSlickSlideFocusability(sliderRootRef.current);
  }, []);

  useEffect(() => {
    const animationFrame = window.requestAnimationFrame(updateSlideFocusability);

    return () => window.cancelAnimationFrame(animationFrame);
  }, [enabled, tabIndex, updateSlideFocusability]);

  useEffect(() => {
    const enable = () => setEnabled(true);

    window.addEventListener('scroll', enable, { once: true });
    window.addEventListener('mousemove', enable, { once: true });
    window.addEventListener('touchstart', enable, { once: true });

    return () => {
      window.removeEventListener('scroll', enable);
      window.removeEventListener('mousemove', enable);
      window.removeEventListener('touchstart', enable);
    };
  }, []);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const settings = {
    centerMode: false,
    dots: false,
    infinite: false,
    onInit: updateSlideFocusability,
    onReInit: updateSlideFocusability,
    afterChange: updateSlideFocusability,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 3,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          // centerMode: true,
        },
      },
    ],
  };

  return (
    <div className={styles.latest_news}>
      <div className="container">
        <div className="row">
          <div className="col-12 d-flex flex-column flex-lg-row align-items-center justify-content-between">
            <h2>Últimas notícias</h2>

            {/* <div className={styles.latest_news_tabs}>
              <NewsTabs value={tabIndex} onChange={handleChange} aria-label="ant example">
                {props.categories.map((category: any, index: number) => (
                  <NewsTab key={index} label={category} />
                ))}
              </NewsTabs>
            </div> */}
          </div>
        </div>

        <div className="row news-carousel">
          <div className={styles.holder_content_tab} ref={sliderRootRef}>
            {props.tabPanel.map((news: any, index: number) => (
              <TabPanel key={index} value={tabIndex} index={index}>
                <SlickSlider
                  key={index}
                  {...settings}
                  className={styles.noticia_list}
                  onInit={updateSlideFocusability}
                  onReInit={updateSlideFocusability}
                  afterChange={updateSlideFocusability}
                >
                  {news.map((data: any, index: number) => (
                    <NewsCard
                      key={index}
                      data={{ ...data, imgpriority: index < 3, imgquality: index < 3 ? 70 : 55, renderImg: enabled }}
                    ></NewsCard>
                  ))}
                </SlickSlider>
              </TabPanel>
            ))}
            <div className="d-flex justify-content-center">
              <a
                href="https://noticias.oamarelinho.com.br"
                target="_blank"
                className="btn btn-secondary btn-medium"
                rel="noreferrer"
              >
                Ver todas as notícias
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
